// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState } from 'react'
import Box from '@mui/material/Box'
import AppDialog from '../ui/AppDialog'
import AppButton from '../ui/AppButton'
import Notice from '../ui/Notice'
import StatusChip from '../ui/StatusChip'
import SignatureCapture, { EMPTY_SIGNATURE, isSignatureComplete, type SignatureValue } from '../ui/SignatureCapture'
import { useInAppDocument, useInAppEnvelope, useReauthKind, useSignInApp } from '../../hooks/queries/inappEsign'
import type { InAppSigner } from '../../api/inappEsign'
import { friendlyError } from '../../api/errors'

/**
 * D-ESIGN-1 — sign a document inside OwlRx (pharmacy staff in their own session). A provider signer signs on their
 * OwlRx provider portal when the prescriber has a login (D-POT-8); without one, their part goes through DocuSign.
 * Shows the exact document being signed (its SHA-256 goes back with the signature), who signs in what order,
 * and — when the signed-in user is the next signer — the signing standard: intent, typed name, optional
 * drawn mark, re-auth. Nothing is emailed or linked anywhere.
 */

export const SIGN_ERRORS: Record<string, string> = {
  reauth_required: 'Re-enter your password or authenticator code to sign.',
  reauth_failed: 'That password or code is not right. Nothing was signed.',
  reauth_locked: 'Too many attempts. Signing is locked for 15 minutes on this document.',
  mfa_required_to_sign: 'Signing needs an authenticator app — set up MFA first.',
  reauth_unavailable: 'Your account signs in with SSO and has no MFA — set up an authenticator app to sign in OwlRx.',
  document_changed: 'The document changed since you opened it. Close and open it again.',
  out_of_order: 'Another signer has to sign first.',
  already_signed: 'This signature is already on the document.',
  signing_busy: 'Another signing attempt for this signature is still being checked. Try again in a moment.',
  envelope_not_open: 'This document is no longer open for signature.',
  not_your_signature: 'This signature belongs to someone else.',
  intent_required: 'Tick the statement to sign.',
  typed_name_required: 'Type your full name.',
  invalid_signature_image: 'The drawn signature could not be read — clear it and draw again.',
}

export function signErrorText(e: unknown): string {
  const code = (e as { body?: { error?: string } }).body?.error
  return (code && SIGN_ERRORS[code]) || friendlyError(e)
}

const ROLE_LABEL: Record<InAppSigner['role'], string> = {
  pharmacist: 'Pharmacist', nurse: 'Nurse', provider: 'Prescriber', patient: 'Patient', interpreter: 'Interpreter',
}

export function signerWaitText(s: InAppSigner): string | null {
  if (s.status !== 'pending') return null
  // D-POT-8: a prescriber with an OwlRx login signs on the provider portal; one without a login cannot sign in OwlRx.
  if (s.waiting_reason === 'not_on_portal') return 'Awaiting provider — no OwlRx login; send the provider part with DocuSign'
  if (s.waiting_reason === 'waiting_for_previous_signer') return 'Signs after the previous signer'
  if (s.channel === 'provider_portal') return 'Signs on their OwlRx provider portal'
  if (s.channel === 'in_person') return 'Signs in person on a staff device'
  return 'Signs in their own OwlRx session'
}

export default function InAppSignDialog({ envelopeId, onClose, onSigned }: {
  envelopeId: string
  onClose: () => void
  onSigned?: (completed: boolean) => void
}) {
  const env = useInAppEnvelope(envelopeId)
  const doc = useInAppDocument(envelopeId)
  const reauth = useReauthKind()
  const sign = useSignInApp(envelopeId)
  const [value, setValue] = useState<SignatureValue>(EMPTY_SIGNATURE)
  const [done, setDone] = useState<string | null>(null)

  const me = env.data?.signers.find(s => s.can_sign_self) ?? null
  const ready = !!me && !!doc.data && reauth !== null && isSignatureComplete(value, reauth)

  const submit = () => {
    if (!me || !doc.data || !reauth) return
    sign.mutate({
      signerId: me.id,
      body: {
        signature_png: value.drawing, typed_name: value.typedName.trim(), intent_accepted: value.intentAccepted,
        document_sha256: doc.data.sha256,
        ...(reauth === 'totp' ? { totp_code: value.totpCode.trim() } : { password: value.password }),
      },
    }, {
      onSuccess: r => {
        setValue(EMPTY_SIGNATURE)
        setDone(r.completed ? 'Signed. Every signature is in — the signed copy and the certificate are filed on the chart.'
          : 'Signed. Your signature is on the document below, on your signature line. The next signer signs this copy.')
        onSigned?.(r.completed)
      },
    })
  }

  return (
    <AppDialog open onClose={onClose} busy={sign.isPending} maxWidth="md"
      title={`Sign in OwlRx — ${env.data?.subject_label ?? 'document'}`}
      actions={<>
        <AppButton onClick={onClose} disabled={sign.isPending}>Close</AppButton>
        {me && !done && <AppButton variant="contained" disabled={!ready || sign.isPending} onClick={submit}>
          {sign.isPending ? 'Signing…' : 'Sign'}</AppButton>}
      </>}>
      {env.isError && <Notice severity="error">{friendlyError(env.error)}</Notice>}
      {env.data && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
            {env.data.patient_name ?? 'Patient'}{env.data.drug_name ? ` · ${env.data.drug_name}` : ''} · signed inside OwlRx — no email or link is sent.
          </Box>
          <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }} aria-label="Signers">
            {env.data.signers.map(s => (
              <Box component="li" key={s.id} data-testid={`signer-${s.role}`} sx={{ fontSize: 13 }}>
                <b>{ROLE_LABEL[s.role]}</b> — {s.display_name}{' '}
                <StatusChip status={s.status === 'signed' ? 'signed' : 'pending'} label={s.status === 'signed' ? 'Signed' : 'Awaiting signature'} />
                {signerWaitText(s) && <Box component="span" sx={{ ml: 1, fontSize: 12, color: s.waiting_reason === 'not_on_portal' ? '#b45309' : 'var(--color-text-mute)' }}>
                  {signerWaitText(s)}</Box>}
              </Box>
            ))}
          </Box>
          {doc.isError && <Notice severity="error">The document could not be loaded: {friendlyError(doc.error)}</Notice>}
          {doc.data && (
            <Box>
              <Box component="iframe" title={env.data.signers.some(s => s.status === 'signed') ? 'Document signed so far' : 'Document being signed'} src={doc.data.url}
                sx={{ width: '100%', height: 360, border: '1px solid var(--color-border)', borderRadius: 1 }} />
              <Box sx={{ fontSize: 11.5, color: 'var(--color-text-mute)', wordBreak: 'break-all' }}>
                Document fingerprint (SHA-256): {doc.data.sha256}
              </Box>
            </Box>
          )}
          {done && <Notice severity="success">{done}</Notice>}
          {sign.isError && <Notice severity="error">{signErrorText(sign.error)}</Notice>}
          {me && !done && me.intent_text && reauth && (
            <SignatureCapture value={value} onChange={setValue} intentText={me.intent_text} reauth={reauth} disabled={sign.isPending} />
          )}
          {!me && !done && env.data.status === 'sent' && (
            <Notice severity="info">You are not the next signer on this document. It shows here so you can follow it.</Notice>
          )}
        </Box>
      )}
    </AppDialog>
  )
}
